import React, { useState, useEffect } from 'react'
import { MapContainer, TileLayer, Marker, Polygon, Popup, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import { fetchWithAuth } from '../utils/api'
import { addDynamicColorClass, generateSafeClassName } from '../utils/dynamicStyles'
import { BASEMAPS } from '../utils/basemaps'
import BaseMapSwitcher from './BaseMapSwitcher'
import { useBaseMap } from '../context/BaseMapContext'

// Fix Leaflet marker icon paths
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/images/marker-icon-2x.png',
  iconUrl: '/images/marker-icon.png',
  shadowUrl: '/images/marker-shadow.png',
})

// Custom markers for different asset types
type AssetTypeInfo = { icon?: string; color?: string }
const createAssetIcon = (type: string, typeInfo: AssetTypeInfo, status: string) => {
  const baseColor = typeInfo.color || '#666'
  const statusColor = status === 'active' ? baseColor : '#95a5a6'
  const completedBadge = status === 'completed' ? '<div class="marker-badge completed">✓</div>' : ''
  
  // Add dynamic color class to document head
  const colorClass = generateSafeClassName('asset-color', `${type}-${status}`);
  addDynamicColorClass(colorClass, statusColor);
  
  return new L.DivIcon({
    html: `
      <div class="custom-marker-base ${colorClass}">
        <span class="marker-icon">${typeInfo.icon || '📦'}</span>
        ${completedBadge}
      </div>
    `,
    className: 'custom-asset-marker',
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  })
}

interface MapAsset {
  id: number
  name: string
  type: string
  position_type: 'point' | 'polygon'
  position_coords: [number, number] | [number, number][]
  time_started: string | null
  time_ended: string | null
  direction_deg?: number | null
  speed_mps?: number | null
  site_name: string | null
  area_name: string | null
  status: 'active' | 'completed'
}

type AssetTypeMap = Record<string, { name?: string; icon?: string; color?: string; defaultPositionType?: 'point' | 'polygon' }>
interface AssetMapProps {
  assetTypes: AssetTypeMap
  onClose: () => void
  onAssetSelect: (assetId: number) => void
}

function AssetMap({ assetTypes, onClose, onAssetSelect }: AssetMapProps) {
  const [assets, setAssets] = useState<MapAsset[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const { baseId, setBaseId } = useBaseMap()

  const fetchMapAssets = React.useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (statusFilter) params.append('status', statusFilter)
      if (typeFilter) params.append('type', typeFilter)

      const response = await fetchWithAuth(`/api/assets/map?${params.toString()}`)
      if (!response.ok) {
        throw new Error('Failed to fetch map assets')
      }
      const data = await response.json()
      setAssets(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load map assets')
    } finally {
      setLoading(false)
    }
  }, [statusFilter, typeFilter])

  useEffect(() => {
    fetchMapAssets()
  }, [fetchMapAssets])

  const getAssetTypeInfo = (type: string) => {
    return assetTypes[type] || { name: type, icon: '📦', color: '#666' }
  }

  if (loading) {
    return (
      <div className="task-map-overlay">
        <div className="task-map-container">
          <div className="loading">Loading map...</div>
        </div>
      </div>
    )
  }

  // Calculate map center based on all asset locations (both points and polygons)
  const calculateMapCenter = (): [number, number] => {
    if (assets.length === 0) return [59.3293, 18.0686] // Default to Stockholm
    
    let totalLat = 0
    let totalLng = 0
    let pointCount = 0
    
    assets.forEach(asset => {
      if (asset.position_type === 'point') {
        const [lng, lat] = asset.position_coords as [number, number] // stored as [lng, lat]
        totalLat += lat
        totalLng += lng
        pointCount++
      } else if (asset.position_type === 'polygon') {
        // Calculate centroid of polygon
        const coords = asset.position_coords as [number, number][] // stored as [lng, lat]
        if (coords.length > 0) {
          // coords are stored as [lng, lat], so coord[1] is lat, coord[0] is lng
          const polygonLat = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length
          const polygonLng = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length
          totalLat += polygonLat
          totalLng += polygonLng
          pointCount++
        }
      }
    })
    
    return pointCount > 0 ? [totalLat / pointCount, totalLng / pointCount] : [59.3293, 18.0686]
  }
  
  const mapCenter = calculateMapCenter()

  const destinationPoint = (lat: number, lng: number, bearingDeg: number, distanceM: number): [number, number] => {
    const R = 6371000
    const br = (bearingDeg * Math.PI) / 180
    const lat1 = (lat * Math.PI) / 180
    const lng1 = (lng * Math.PI) / 180
    const dr = distanceM / R
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(dr) + Math.cos(lat1) * Math.sin(dr) * Math.cos(br)
    )
    const lng2 = lng1 + Math.atan2(
      Math.sin(br) * Math.sin(dr) * Math.cos(lat1),
      Math.cos(dr) - Math.sin(lat1) * Math.sin(lat2)
    )
    return [(lat2 * 180) / Math.PI, ((lng2 * 180) / Math.PI + 540) % 360 - 180]
  }

  const VectorArrow: React.FC<{ id: number; start: [number, number]; dir: number; speed: number }>
    = ({ id, start, dir, speed }) => {
    // use Leaflet map to compute pixel length threshold
     
    const map = useMap()
    const lengthM = Math.max(5, Math.min(150, speed * 12))
    const end = destinationPoint(start[0], start[1], dir, lengthM)
    try {
      const p1 = map.latLngToLayerPoint(start as L.LatLngExpression)
      const p2 = map.latLngToLayerPoint(end as L.LatLngExpression)
      const dx = p2.x - p1.x, dy = p2.y - p1.y
      const pix = Math.sqrt(dx*dx + dy*dy)
      if (pix < 28) return null
    } catch {}
    return (
      <>
        <Polyline key={`vec-${id}`} positions={[start, end]} pathOptions={{ color: '#ff2d55', weight: 8, opacity: 0.98, className: 'vector-line' }} />
        <Marker key={`arrow-${id}`} position={end} icon={L.divIcon({
          html: `
            <svg width=\"28\" height=\"28\" viewBox=\"0 0 28 28\" style=\"transform: rotate(${(dir - 90)}deg); transform-origin: 100% 50%; display:block;\">
              <polygon points=\"0,6 0,22 28,14\" style=\"fill:#ff2d55; stroke:#ff2d55; stroke-width:1\" />
            </svg>
          `,
          className: 'vector-arrow-icon',
          iconSize: [28, 28],
          iconAnchor: [28, 14],
        })} />
      </>
    )
  }

  return (
    <div className="task-map-overlay">
      <div className="task-map-container">
        <div className="task-map-header">
          <h3>🗺️ Asset Map</h3>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {error && <div className="error">{error}</div>}

        <div className="map-filters">
          <div className="filter-group">
            <label>Status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Type:</label>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All Types</option>
              {Object.entries(assetTypes).map(([key, type]) => (
                <option key={key} value={key}>
                  {type.icon} {type.name}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group" style={{ marginLeft: 'auto' }}>
            <label>Base map:</label>
            <BaseMapSwitcher value={baseId} onChange={setBaseId} />
          </div>
        </div>

        {/* Legend removed to reduce clutter; use Type filter instead */}

        <div className="map-container-wrapper">
          {assets.length === 0 ? (
            <div className="no-tasks-message">
              <p>No assets with locations found</p>
              <p>Create assets to see them on the map</p>
            </div>
          ) : (
            <MapContainer
              center={mapCenter}
              zoom={10}
              style={{ height: '500px', width: '100%' }}
            >
              <TileLayer
                key={baseId}
                url={BASEMAPS[baseId].url}
                attribution={BASEMAPS[baseId].attribution}
                {...(BASEMAPS[baseId].options || {})}
              />
              
              {assets.map((asset) => {
                const typeInfo = getAssetTypeInfo(asset.type)
                
                if (asset.position_type === 'point') {
                  const lat = asset.position_coords[1] as number
                  const lng = asset.position_coords[0] as number
                  // vector rendered below after markers
                  return (
                    <Marker
                      key={asset.id}
                      position={[lat, lng] as [number, number]} // Convert [lng, lat] to [lat, lng]
                      icon={createAssetIcon(asset.type, typeInfo, asset.status)}
                    >
                      <Popup>
                        <div className="task-popup">
                          <h4>{asset.name}</h4>
                          <div className="popup-meta">
                            <div className="popup-badges">
                              <span 
                                className="priority-badge" 
                                style={{ backgroundColor: typeInfo.color }}
                              >
                                {typeInfo.icon} {typeInfo.name}
                              </span>
                              <span 
                                className="priority-badge"
                                style={{ 
                                  backgroundColor: asset.status === 'active' ? '#27ae60' : '#95a5a6' 
                                }}
                              >
                                {asset.status}
                              </span>
                            </div>
                            {asset.site_name && (
                              <p className="popup-location">
                                🏠 {asset.site_name}
                                {asset.area_name && ` → 📍 ${asset.area_name}`}
                              </p>
                            )}
                            {typeof asset.direction_deg === 'number' && (
                              <p className="popup-location">🧭 Direction: {Math.round(((asset.direction_deg % 360) + 360) % 360)}°</p>
                            )}
                            {typeof asset.speed_mps === 'number' && (
                              <p className="popup-location">🏃 Speed: {asset.speed_mps.toFixed(1)} m/s</p>
                            )}
                          </div>
                          <button 
                            className="btn btn-sm btn-success"
                            onClick={() => onAssetSelect(asset.id)}
                          >
                            View Details
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  )
                } else {
                  return (
                    <React.Fragment key={asset.id}>
                    {/* existing polygon rendering below */}
                    </React.Fragment>
                  )
                }
              })}
              {assets.map((asset) => {
                const typeInfo = getAssetTypeInfo(asset.type)
                if (asset.position_type === 'polygon') {
                  // Polygon assets
                  const coords = asset.position_coords as [number, number][] // stored as [lng, lat]
                  // Convert [lng, lat] to [lat, lng] for Leaflet
                  const leafletCoords = coords.map(coord => [coord[1], coord[0]] as [number, number])
                  // Calculate polygon centroid for marker
                  const centroidLat = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length
                  const centroidLng = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length
                  const centroid: [number, number] = [centroidLat, centroidLng]
                  
                  return (
                    <React.Fragment key={asset.id}>
                      <Polygon
                        positions={leafletCoords}
                        pathOptions={{
                          color: typeInfo.color,
                          fillColor: typeInfo.color,
                          fillOpacity: asset.status === 'active' ? 0.5 : 0.3,
                          weight: 4,
                          opacity: asset.status === 'active' ? 0.9 : 0.6,
                          dashArray: asset.status === 'completed' ? '10, 5' : undefined
                        }}
                      >
                        <Popup>
                        <div className="task-popup">
                          <h4>{asset.name}</h4>
                          <div className="popup-meta">
                            <div className="popup-badges">
                              <span 
                                className="priority-badge" 
                                style={{ backgroundColor: typeInfo.color }}
                              >
                                {typeInfo.icon} {typeInfo.name}
                              </span>
                              <span 
                                className="priority-badge"
                                style={{ 
                                  backgroundColor: asset.status === 'active' ? '#27ae60' : '#95a5a6' 
                                }}
                              >
                                {asset.status}
                              </span>
                            </div>
                            {asset.site_name && (
                              <p className="popup-location">
                                🏠 {asset.site_name}
                                {asset.area_name && ` → 📍 ${asset.area_name}`}
                              </p>
                            )}
                            <p className="popup-location">
                              🟦 Polygon area ({(asset.position_coords as [number, number][]).length} points)
                            </p>
                          </div>
                          <button 
                            className="btn btn-sm btn-success"
                            onClick={() => onAssetSelect(asset.id)}
                          >
                            View Details
                          </button>
                          </div>
                        </Popup>
                      </Polygon>
                      
                      {/* Center marker for polygon assets */}
                      <Marker
                        position={centroid}
                        icon={createAssetIcon(asset.type, typeInfo, asset.status)}
                      >
                        <Popup>
                          <div className="task-popup">
                            <h4>{asset.name}</h4>
                            <div className="popup-meta">
                              <div className="popup-badges">
                                <span 
                                  className="priority-badge" 
                                  style={{ backgroundColor: typeInfo.color }}
                                >
                                  {typeInfo.icon} {typeInfo.name}
                                </span>
                                <span 
                                  className="priority-badge"
                                  style={{ 
                                    backgroundColor: asset.status === 'active' ? '#27ae60' : '#95a5a6' 
                                  }}
                                >
                                  {asset.status}
                                </span>
                              </div>
                              {asset.site_name && (
                                <p className="popup-location">
                                  🏠 {asset.site_name}
                                  {asset.area_name && ` → 📍 ${asset.area_name}`}
                                </p>
                              )}
                              <p className="popup-location">
                                🟦 Polygon area ({coords.length} points)
                              </p>
                            </div>
                            <button 
                              className="btn btn-sm btn-success"
                              onClick={() => onAssetSelect(asset.id)}
                            >
                              View Details
                            </button>
                          </div>
                        </Popup>
                      </Marker>
                    </React.Fragment>
                  )
                }
              })}
              {/* Direction vectors for point assets */}
              {assets.map((asset) => {
                if (asset.position_type !== 'point') return null
                if (typeof asset.direction_deg !== 'number' || typeof asset.speed_mps !== 'number') return null
                // Only show vector if observation is less than 24 hours old
                if (asset.time_started) {
                  const ageMs = Date.now() - new Date(asset.time_started).getTime()
                  if (ageMs > 24 * 60 * 60 * 1000) return null
                }
                const start: [number, number] = [asset.position_coords[1] as number, asset.position_coords[0] as number]
                return <VectorArrow key={`vec-${asset.id}`} id={asset.id} start={start} dir={asset.direction_deg as number} speed={asset.speed_mps as number} />
              })}
            </MapContainer>
          )}
        </div>

        <div className="map-info">
          <p>Showing {assets.length} asset{assets.length !== 1 ? 's' : ''} on the map</p>
          <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.5rem' }}>
            <span style={{ marginRight: '1rem' }}>📍 Point assets: {assets.filter(a => a.position_type === 'point').length}</span>
            <span>🟦 Area assets: {assets.filter(a => a.position_type === 'polygon').length}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AssetMap
